import type { CSSProperties, FC, ReactNode } from 'react'

import Link from 'next/link'
import {
    ArrowRight,
    Bot,
    ChartLine,
    CircleCheck,
    Eye,
    FileText,
    LayoutDashboard,
    Lock,
    Sparkles,
    Timer
} from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Container } from '@/components/generals/container'
import { ButtonLink } from '@/components/generals/button'
import { PageHero } from '@/components/sections/page-hero'
import { SplitFeature } from '@/components/sections/split-feature'
import { SecurityGrid } from '@/components/sections/security-grid'
import { ProductDevice } from '@/components/three/product-device'
import { DashboardShowcase } from '@/components/landing/dashboard-showcase'
import { AutomationFlow } from '@/components/product/automation-flow'
import { ReportingPreview } from '@/components/features/reporting-preview'
import { ForecastPreview } from '@/components/features/forecast-preview'
import { RevenuePreview } from '@/components/features/revenue-preview'
import { FEATURES } from '@/constants/features'

const PILLARS = [
    { href: '#dashboard', label: 'Dashboard', body: 'Every account and metric in one live view.', Icon: LayoutDashboard },
    { href: '#automation', label: 'AI automation', body: 'Categorize, match and reconcile on autopilot.', Icon: Bot },
    { href: '#reporting', label: 'Reporting', body: 'Statements and board packs that write themselves.', Icon: FileText },
    { href: '#forecasting', label: 'Forecasting and analytics', body: 'See cash and revenue months ahead.', Icon: ChartLine }
]

const CHIP = 'pointer-events-none absolute hidden items-center gap-2 rounded-full border border-burgundy/10 bg-ivory/90 py-1.5 pl-1.5 pr-3.5 text-xs text-burgundy shadow-[0_14px_30px_-18px_rgb(57_26_24/0.5)] backdrop-blur sm:flex'

const Product: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title='Product'
                description='Explore the Veyra platform: a live financial dashboard, AI automation that reconciles your books, reports that write themselves, and forecasting and analytics you can act on.'
            />

            <PageHero
                eyebrow='Product'
                title={<>The financial command center for <span className='text-stone'>modern companies.</span></>}
                description='Veyra connects every account, automates the busywork and turns your numbers into decisions. One platform for reporting, spend, forecasting and analytics.'
                actions={(
                    <>
                        <ButtonLink href='/signup' size='lg' arrow>Start free trial</ButtonLink>
                        <ButtonLink href='/demo' variant='secondary' size='lg'>Request demo</ButtonLink>
                    </>
                )}
                aside={(
                    <ProductDevice>
                        <span aria-hidden='true' className={`${CHIP} animate-float left-[2%] top-[14%]`}>
                            <span className='flex size-6 items-center justify-center rounded-full bg-sage/25 text-sage-dark'>
                                <CircleCheck className='size-3.5' />
                            </span>
                            12 accounts synced
                        </span>

                        <span aria-hidden='true' className={`${CHIP} animate-float-slow right-[2%] top-[30%]`}>
                            <span className='flex size-6 items-center justify-center rounded-full bg-burgundy text-gold'>
                                <Sparkles className='size-3.5' />
                            </span>
                            3 new insights
                        </span>

                        <span aria-hidden='true' className={`${CHIP} animate-float bottom-[10%] left-[10%]`} style={{ animationDelay: '-3s' }}>
                            <span className='flex size-6 items-center justify-center rounded-full bg-gold/25 text-gold-dark'>
                                <Timer className='size-3.5' />
                            </span>
                            Month closed in 4 days
                        </span>
                    </ProductDevice>
                )}
            >
                <ul className='grid border-l border-t border-burgundy/10 sm:grid-cols-2 lg:grid-cols-4'>
                    {PILLARS.map(({ href, label, body, Icon }) => (
                        <li key={href} className='border-b border-r border-burgundy/10'>
                            <a href={href} className='group flex h-full flex-col p-6 transition-colors duration-300 hover:bg-ivory/70'>
                                <span className='flex items-center justify-between'>
                                    <span className='flex size-10 items-center justify-center rounded-full bg-burgundy text-gold'>
                                        <Icon aria-hidden='true' className='size-4' />
                                    </span>
                                    <ArrowRight aria-hidden='true' className='size-4 text-stone transition-transform duration-300 group-hover:translate-x-0.5 group-hover:text-burgundy' />
                                </span>
                                <span className='mt-6 font-medium text-burgundy'>{label}</span>
                                <span className='mt-1.5 text-sm text-stone-dark'>{body}</span>
                            </a>
                        </li>
                    ))}
                </ul>
            </PageHero>

            <DashboardShowcase
                eyebrow='The dashboard'
                title='Every number your team needs, in one place.'
                description='Income, expenses, cash, categories, transactions, forecasts and AI insights, all reconciled from the same data and updated throughout the day.'
                link={false}
            />

            <SplitFeature
                id='automation'
                label='AI automation'
                title='Automation that does the busywork.'
                description='Describe a rule in plain language and Veyra builds it. Transactions are categorized, receipts matched and accounts reconciled continuously, with every action logged and reversible.'
                points={[
                    { text: 'Rules built from a sentence, refined with a click', Icon: Bot },
                    { text: 'Uncertain items routed to a person, never guessed', Icon: Eye },
                    { text: 'Every automated action is traceable and reversible', Icon: Lock }
                ]}
                visual={<AutomationFlow />}
                tone='cream'
            />

            <SplitFeature
                id='reporting'
                label='Reporting'
                title='Reports that write themselves.'
                description='Profit and loss, balance sheet and cash flow statements are generated the moment your books close. AI drafts the commentary, your team reviews and sends.'
                points={[
                    'Scheduled delivery to email and Slack',
                    'Board-ready exports to PDF, Excel and Google Sheets',
                    'Commentary drafted for every material variance'
                ]}
                visual={<ReportingPreview />}
                reverse
                tone='burgundy'
                link={{ href: '/features#automated-reporting', label: 'Explore reporting' }}
            />

            <SplitFeature
                id='forecasting'
                label='Forecasting'
                title='See your cash position months ahead.'
                description='A rolling forecast built from how customers actually pay, recurring bills and payroll. Compare scenarios side by side before you commit to a decision.'
                points={[
                    '13-week and 12-month horizons',
                    'Base, conservative and growth scenarios',
                    'Forecast versus actual tracking every week'
                ]}
                visual={<ForecastPreview />}
                tone='cream'
                link={{ href: '/features#cash-flow-forecasting', label: 'Explore forecasting' }}
            />

            <SplitFeature
                id='analytics'
                label='Analytics'
                title='Understand what drives your growth.'
                description='Recurring revenue, retention and expansion are calculated automatically from billing, CRM and ledger data. Break any metric down by segment, plan or cohort in a click.'
                points={[
                    'MRR, ARR and net revenue retention',
                    'Cohort and segment breakdowns',
                    'Hover any period to see exactly what changed'
                ]}
                visual={<RevenuePreview />}
                reverse
                tone='paper'
                link={{ href: '/features#revenue-analytics', label: 'Explore analytics' }}
            />

            <SecurityGrid />

            <section aria-labelledby='all-features-title' className='bg-cream'>
                <Container className='py-24 md:py-32'>
                    <div className='flex flex-col items-start justify-between gap-8 border-b border-burgundy/10 pb-10 md:flex-row md:items-end'>
                        <h2 id='all-features-title' data-reveal='' className='max-w-xl font-heading text-display-md font-medium text-burgundy'>
                            Six capabilities, one connected platform.
                        </h2>

                        <ButtonLink href='/features' variant='secondary' arrow>
                            See all features
                        </ButtonLink>
                    </div>

                    <ul className='grid sm:grid-cols-2 lg:grid-cols-3'>
                        {FEATURES.map(({ id, number, title, summary, Icon }, index) => (
                            <li
                                key={id}
                                data-reveal=''
                                className='border-b border-burgundy/10'
                                style={{ '--reveal-delay': `${(index % 3) * 80}ms` } as CSSProperties}
                            >
                                <Link href={`/features#${id}`} className='group flex h-full flex-col gap-4 py-8 pr-8 transition-colors duration-300'>
                                    <span className='flex items-center gap-3 font-mono text-xs text-stone'>
                                        {number}
                                        <Icon aria-hidden='true' className='size-4 text-gold-dark' />
                                    </span>
                                    <span className='text-lg font-medium text-burgundy transition-colors duration-300 group-hover:text-burgundy-muted'>{title}</span>
                                    <span className='text-sm leading-relaxed text-stone-dark'>{summary}</span>
                                </Link>
                            </li>
                        ))}
                    </ul>
                </Container>
            </section>
        </Layout>
    )
}

export default Product
